import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Users as UsersIcon, Car as CarIcon, Sparkles, ExternalLink } from "lucide-react";
import { PLANNING_CATEGORIES, TEAM_COLORS, getCategoryLabel } from "../../lib/constants";
import { ResourcePreview } from "./ResourcePreview";
import { useConfirm } from "../ConfirmProvider";
import { fmtRoDate } from "../../lib/dateFmt";

export function ScheduleDialog({ mode, entry, initialStart, initialEnd, prefilledPersonId, prefillEntry, projects, regii = [], allPersons = [], allCars = [], dailyNeeds = [], month, existingEntries = [], regieWeeksMap = {}, onClose, onSaved, onProjectsRefresh }) {
  const navigate = useNavigate();
  const lookupRegieWeek = (regieId, weekStart) => {
    if (!regieId || !weekStart) return null;
    const data = regieWeeksMap[regieId];
    if (!data) return null;
    if (Array.isArray(data)) return data.find((w) => w.week_start === weekStart) || null;
    return data[weekStart] || null;
  };
  const weekStartOf = (dStr) => {
    if (!dStr) return null;
    const dt = new Date(dStr);
    const dow = dt.getDay();
    const diff = (dow + 6) % 7;
    const m = new Date(dt);
    m.setDate(dt.getDate() - diff);
    return m.toISOString().slice(0, 10);
  };
  const regiePrefill = (regieId, startDate) => {
    if (!regieId) return null;
    const wk = lookupRegieWeek(regieId, weekStartOf(startDate));
    if (wk && (wk.min_people != null || wk.max_people != null)) {
      return { min: wk.min_people ?? "", max: wk.max_people ?? "" };
    }
    const rg = regii.find((x) => x.id === regieId);
    if (rg) {
      return {
        min: rg.default_min_people ?? "",
        max: rg.default_max_people ?? "",
      };
    }
    return null;
  };

  const [tab, setTab] = useState(() => {
    if (mode === "edit" && entry?.is_regie) return "regie";
    if (prefillEntry?.is_regie) return "regie";
    return "project";
  });
  const [form, setForm] = useState(() => {
    if (mode === "edit" && entry) {
      let minP = entry.min_people ?? "";
      let maxP = entry.max_people ?? "";
      if (entry.is_regie && entry.regie_id) {
        const pre = regiePrefill(entry.regie_id, entry.start_date);
        if (pre) { minP = pre.min; maxP = pre.max; }
      }
      return {
        project_id: entry.project_id || "",
        label: entry.label || "",
        start_date: entry.start_date,
        end_date: entry.end_date,
        color: entry.color || "#3B82F6",
        note: entry.note || "",
        is_regie: !!entry.is_regie,
        person_ids: entry.person_ids || [],
        car_ids: entry.car_ids || [],
        regie_id: entry.regie_id || "",
        min_people: minP,
        max_people: maxP,
      };
    }
    if (prefillEntry) {
      let minP = "", maxP = "";
      if (prefillEntry.is_regie && prefillEntry.regie_id) {
        const pre = regiePrefill(prefillEntry.regie_id, prefillEntry.start_date || initialStart);
        if (pre) { minP = pre.min; maxP = pre.max; }
      }
      return {
        project_id: prefillEntry.project_id || "",
        label: prefillEntry.label || "",
        start_date: prefillEntry.start_date || initialStart,
        end_date: prefillEntry.end_date || initialEnd,
        color: prefillEntry.color || "#3B82F6",
        note: "",
        is_regie: !!prefillEntry.is_regie,
        person_ids: prefilledPersonId ? [prefilledPersonId] : (prefillEntry.person_ids || []),
        car_ids: prefillEntry.car_ids || [],
        regie_id: prefillEntry.regie_id || "",
        min_people: minP,
        max_people: maxP,
      };
    }
    return {
      project_id: "", label: "", start_date: initialStart, end_date: initialEnd,
      color: "#3B82F6", note: "", is_regie: false,
      person_ids: prefilledPersonId ? [prefilledPersonId] : [],
      car_ids: [], regie_id: "",
      min_people: "", max_people: "",
    };
  });
  const [newProjOpen, setNewProjOpen] = useState(false);
  const [newProj, setNewProj] = useState({ name: "", category: "5", description: "" });
  const [suggestions, setSuggestions] = useState({ persons: [], cars: [] });
  // Detalii complete proiect/regie pentru afișare în panoul "Detalii șantier"
  const [projectDetails, setProjectDetails] = useState(null);
  const [regieDetails, setRegieDetails] = useState(null);
  useEffect(() => {
    if (form.project_id) {
      api.get(`/projects/${form.project_id}`).then((r) => setProjectDetails(r.data)).catch(() => setProjectDetails(null));
      setRegieDetails(null);
    } else if (form.regie_id) {
      api.get(`/regii/${form.regie_id}`).then((r) => setRegieDetails(r.data)).catch(() => setRegieDetails(null));
      setProjectDetails(null);
    } else {
      setProjectDetails(null);
      setRegieDetails(null);
    }
  }, [form.project_id, form.regie_id]);

  const usedProjectIds = useMemo(() => {
    const s = new Set();
    for (const e of existingEntries) {
      if (e.project_id && (mode !== "edit" || entry?.id !== e.id)) s.add(e.project_id);
    }
    return s;
  }, [existingEntries, mode, entry]);
  const usedRegieIds = useMemo(() => {
    const s = new Set();
    for (const e of existingEntries) {
      if (e.regie_id && (mode !== "edit" || entry?.id !== e.id)) s.add(e.regie_id);
    }
    return s;
  }, [existingEntries, mode, entry]);

  const planningProjects = projects.filter(
    (p) => PLANNING_CATEGORIES.includes(p.category) && (!usedProjectIds.has(p.id) || p.id === form.project_id)
  );
  const availableRegii = regii.filter(
    (r) => r.active !== false && (!usedRegieIds.has(r.id) || r.id === form.regie_id)
  );

  useEffect(() => {
    if (!form.project_id && !form.color) { setSuggestions({ persons: [], cars: [] }); return; }
    const params = new URLSearchParams();
    if (form.project_id) params.set("project_id", form.project_id);
    if (form.color) params.set("color", form.color);
    params.set("days", "60");
    api.get(`/schedule/suggest?${params.toString()}`)
      .then((r) => setSuggestions(r.data || { persons: [], cars: [] }))
      .catch(() => setSuggestions({ persons: [], cars: [] }));
  }, [form.project_id, form.color]);

  const onSelectProject = (pid) => {
    const p = projects.find((x) => x.id === pid);
    setForm((x) => ({ ...x, project_id: pid, label: p ? p.name : x.label, color: p?.color || x.color }));
  };

  const onSelectRegie = (rid) => {
    const rg = regii.find((x) => x.id === rid);
    setForm((x) => {
      const pre = regiePrefill(rid, x.start_date);
      return {
        ...x,
        regie_id: rid,
        label: rg ? rg.name : x.label,
        color: rg?.color || x.color,
        min_people: pre ? pre.min : x.min_people,
        max_people: pre ? pre.max : x.max_people,
      };
    });
  };

  const togglePerson = (pid) => setForm((x) => ({
    ...x,
    person_ids: x.person_ids.includes(pid) ? x.person_ids.filter((p) => p !== pid) : [...x.person_ids, pid],
  }));
  const toggleCar = (cid) => setForm((x) => ({
    ...x,
    car_ids: x.car_ids.includes(cid) ? x.car_ids.filter((c) => c !== cid) : [...x.car_ids, cid],
  }));

  const createAndAttach = async () => {
    if (!newProj.name.trim()) { toast.error("Denumire necesară"); return; }
    try {
      const { data } = await api.post("/projects", { ...newProj });
      await onProjectsRefresh?.();
      setForm((x) => ({ ...x, project_id: data.id, label: data.name }));
      setNewProjOpen(false);
      setNewProj({ name: "", category: "5", description: "" });
      toast.success("Proiect creat și atașat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const submit = async () => {
    if (!form.label) { toast.error("Etichetă obligatorie"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Interval obligatoriu"); return; }
    if (form.start_date > form.end_date) { toast.error("Data început > data sfârșit"); return; }
    const isRegie = tab === "regie";
    const minVal = form.min_people === "" || form.min_people === null || form.min_people === undefined ? null : parseInt(form.min_people);
    const maxVal = minVal; // se completează doar minimul; maximul urmează minimul
    const payload = {
      project_id: isRegie ? null : (form.project_id || null),
      regie_id: isRegie ? (form.regie_id || null) : null,
      label: form.label,
      is_regie: isRegie,
      start_date: form.start_date,
      end_date: form.end_date,
      color: form.color,
      note: form.note || null,
      person_ids: form.person_ids,
      car_ids: form.car_ids,
      min_people: minVal,
      max_people: maxVal,
    };
    try {
      if (mode === "edit" && entry) { await api.put(`/schedule/${entry.id}`, payload); toast.success("Actualizat"); }
      else { await api.post("/schedule", payload); toast.success(`${isRegie ? "Regie" : "Proiect"} adăugat în plan`); }

      if (isRegie && form.regie_id && (minVal != null || maxVal != null)) {
        try {
          const weeks = new Set();
          const start = new Date(form.start_date);
          const end = new Date(form.end_date);
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dow = d.getDay();
            const diff = (dow + 6) % 7;
            const monday = new Date(d);
            monday.setDate(d.getDate() - diff);
            weeks.add(monday.toISOString().slice(0, 10));
          }
          const weekUpdates = Array.from(weeks).map((ws) =>
            api.put(`/regii/${form.regie_id}/weeks/${ws}`, {
              ...(minVal != null ? { min_people: minVal } : {}),
              ...(maxVal != null ? { max_people: maxVal } : {}),
            }).catch(() => {})
          );
          const rg = regii.find((x) => x.id === form.regie_id);
          let regieUpdate = Promise.resolve();
          if (rg) {
            const merged = {
              name: rg.name,
              firma: rg.firma || null,
              adresa: rg.adresa || null,
              color: rg.color || "#F59E0B",
              active: rg.active !== false,
              note: rg.note || null,
              start_date: rg.start_date || null,
              end_date: rg.end_date || null,
              indeterminate_end: !!rg.indeterminate_end,
              default_min_people: minVal != null ? minVal : (rg.default_min_people ?? null),
              default_max_people: maxVal != null ? maxVal : (rg.default_max_people ?? null),
            };
            regieUpdate = api.put(`/regii/${form.regie_id}`, merged).catch(() => {});
          }
          await Promise.all([...weekUpdates, regieUpdate]);
        } catch {}
      }

      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  const remove = async () => {
    if (!(await confirm({ body: "Șterge?", danger: true }))) return;
    await api.delete(`/schedule/${entry.id}`);
    toast.success("Șters");
    onSaved();
  };

  const sortedPersons = [...allPersons]
    .filter((p) => p.active !== false && !p.data_concedierii)
    .sort((a, b) => `${a.nume} ${a.prenume}`.localeCompare(`${b.nume} ${b.prenume}`, "ro"));
  const suggestedPersonIds = suggestions.persons.map((s) => s.person_id);
  const suggestedCarIds = suggestions.cars.map((s) => s.car_id);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Editează intrare plan" : "Adaugă în plan"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {mode !== "edit" && (
            <div className="flex gap-2 p-1 bg-zinc-100 rounded-md">
              <button
                type="button"
                onClick={() => setTab("project")}
                className={`flex-1 py-2 px-4 rounded text-sm font-semibold transition-colors ${tab === "project" ? "bg-white shadow text-[var(--primary)]" : "text-zinc-600 hover:text-zinc-900"}`}
                data-testid="tab-proiect"
              >📁 Proiect</button>
              <button
                type="button"
                onClick={() => setTab("regie")}
                className={`flex-1 py-2 px-4 rounded text-sm font-semibold transition-colors ${tab === "regie" ? "bg-white shadow text-[#F59E0B]" : "text-zinc-600 hover:text-zinc-900"}`}
                data-testid="tab-regie"
              >🔧 Regie</button>
            </div>
          )}

          {tab === "project" ? (
            <div>
              <Label className="small-label">Proiect (categoriile 5 și 6)</Label>
              <div className="flex gap-2 mt-2">
                <Select value={form.project_id || ""} onValueChange={onSelectProject}>
                  <SelectTrigger data-testid="schedule-project">
                    <SelectValue placeholder={planningProjects.length === 0 ? "Toate proiectele eligibile sunt deja în plan — creează unul" : "Selectează proiect"} />
                  </SelectTrigger>
                  <SelectContent>
                    {planningProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{getCategoryLabel(p.category)} — {p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={() => setNewProjOpen((v) => !v)} title="Proiect nou">
                  <Plus size={16}/>
                </Button>
              </div>
              {newProjOpen && (
                <div className="mt-3 p-3 border border-[var(--border)] rounded-md bg-zinc-50 space-y-2">
                  <div className="text-xs font-semibold text-zinc-600">Proiect nou (categoria 5 sau 6)</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input className="col-span-2" placeholder="Denumire șantier" value={newProj.name} onChange={(e) => setNewProj({ ...newProj, name: e.target.value })} />
                    <Select value={newProj.category} onValueChange={(v) => setNewProj({ ...newProj, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 Anmelduite SUVA</SelectItem>
                        <SelectItem value="6">6 Sanierungskonzept</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setNewProjOpen(false)}>Anulează</Button>
                    <Button size="sm" onClick={createAndAttach} className="bg-[var(--primary)]">Creează & atașează</Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label className="small-label">Regie</Label>
              <Select value={form.regie_id || ""} onValueChange={onSelectRegie}>
                <SelectTrigger data-testid="schedule-regie" className="mt-2">
                  <SelectValue placeholder={regii.length === 0 ? "Nicio regie definită" : "Selectează regie"} />
                </SelectTrigger>
                <SelectContent>
                  {availableRegii.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color || "#F59E0B" }}/>
                        {r.name}{r.firma ? ` · ${r.firma}` : ""}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableRegii.length === 0 && (
                <p className="text-[10px] text-zinc-500 mt-1">Toate regiile active sunt deja în plan.</p>
              )}
            </div>
          )}

          <div>
            <Label className="small-label">Etichetă afișată</Label>
            <Input data-testid="schedule-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-2" />
          </div>

          {/* --- Detalii șantier (proiect sau regie) --- */}
          {(projectDetails || regieDetails) && (
            <div className="border border-zinc-200 bg-zinc-50 rounded-md p-3 space-y-1.5" data-testid="site-details-panel">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                  {projectDetails ? "📋 Detalii proiect" : "🛠 Detalii regie"}
                </div>
                {projectDetails && (
                  <button
                    type="button"
                    onClick={() => { onClose && onClose(); navigate(`/proiecte?project=${projectDetails.id}&edit=1`); }}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:underline"
                    data-testid="open-project-page-link"
                  >
                    Editează în „Proiecte" <ExternalLink size={11}/>
                  </button>
                )}
                {regieDetails && (
                  <button
                    type="button"
                    onClick={() => { onClose && onClose(); navigate(`/proiecte?regie=${regieDetails.id}&edit=1`); }}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:underline"
                    data-testid="open-regii-page-link"
                  >
                    Editează în „Regii" <ExternalLink size={11}/>
                  </button>
                )}
              </div>
              {(() => {
                const d = projectDetails || regieDetails || {};
                const rows = [];
                if (d.adresa) rows.push(["📍 Adresă", d.adresa]);
                if (d.auftraggeber) rows.push(["👔 Auftraggeber", d.auftraggeber]);
                if (d.firma) rows.push(["🏢 Firmă", d.firma]);
                if (d.start_date || d.end_date) rows.push(["📅 Perioadă", `${fmtRoDate(d.start_date) || "?"} – ${d.indeterminate_end ? "nedeterminat" : (fmtRoDate(d.end_date) || "?")}`]);
                if (d.min_people || d.default_min_people) rows.push(["👥 Min. persoane", d.min_people || d.default_min_people]);
                if (d.max_people || d.default_max_people) rows.push(["👥 Max. persoane", d.max_people || d.default_max_people]);
                if (d.min_cars) rows.push(["🚗 Min. mașini", d.min_cars]);
                if (d.zone_de_lucru) rows.push(["📐 Zone de lucru", d.zone_de_lucru]);
                if (d.note || d.descriere) rows.push(["📝 Notă", d.note || d.descriere]);
                if (d.special) rows.push(["⚠️ Special", d.special]);
                if (rows.length === 0) {
                  return <div className="text-[11px] text-zinc-500 italic">Nu există detalii suplimentare. Apasă „Editează în Proiecte" pentru a completa.</div>;
                }
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    {rows.map(([k, v]) => (
                      <div key={k} className="flex gap-1.5 items-start">
                        <span className="font-semibold text-zinc-600 whitespace-nowrap">{k}:</span>
                        <span className="text-zinc-800 break-words">{v}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="small-label">De la</Label><Input type="date" data-testid="schedule-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-2" /></div>
            <div><Label className="small-label">Până la</Label><Input type="date" data-testid="schedule-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-2" /></div>
          </div>
          <div>
            <Label className="small-label">Culoare echipă</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {TEAM_COLORS.map((c) => (
                <button key={c.code} type="button" onClick={() => setForm({ ...form, color: c.hex })}
                  data-testid={`color-${c.code}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${form.color === c.hex ? "ring-2 ring-offset-1" : "hover:opacity-80"}`}
                  style={{ background: c.hex, color: "#fff" }}>
                  <span className="w-2 h-2 rounded-full bg-white" />{c.label}
                </button>
              ))}
            </div>
          </div>

          {/* PERSOANE */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="small-label flex items-center gap-2">
                <UsersIcon size={14}/> Persoane atribuite
                {form.person_ids.length > 0 && <span className="text-[10px] font-bold text-[var(--primary)]">({form.person_ids.length})</span>}
              </Label>
              {suggestions.persons.length > 0 && (
                <span className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                  <Sparkles size={11} className="text-amber-500"/> sugestii bazate pe ultimele 60 zile
                </span>
              )}
            </div>
            {suggestions.persons.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5 p-2 bg-amber-50/60 border border-amber-200 rounded-md" data-testid="person-suggestions">
                {suggestions.persons.slice(0, 8).map((s) => {
                  const sel = form.person_ids.includes(s.person_id);
                  return (
                    <button
                      key={s.person_id}
                      type="button"
                      onClick={() => togglePerson(s.person_id)}
                      className={`text-[10px] px-2 py-1 rounded-full border transition-all ${sel ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white text-zinc-700 border-amber-300 hover:bg-amber-100"}`}
                      data-testid={`person-suggest-${s.person_id}`}
                      title={`${s.match === "project" ? "Lucrat pe acest proiect" : "Lucrat pe culoare similară"} · ${s.count}× · ultima dată ${s.last_used}`}
                    >
                      {s.name} <span className="opacity-60">×{s.count}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto border border-[var(--border)] rounded-md p-2" data-testid="person-list">
              {sortedPersons.map((p) => {
                const sel = form.person_ids.includes(p.id);
                const suggested = suggestedPersonIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePerson(p.id)}
                    data-testid={`schedule-person-${p.id}`}
                    className={`text-left text-xs px-2 py-1 rounded transition-colors flex items-center gap-1.5 ${sel ? "bg-[var(--primary)] text-white" : suggested ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-zinc-50"}`}
                  >
                    {suggested && <Sparkles size={10} className={sel ? "text-white" : "text-amber-500"}/>}
                    <span className="truncate">{p.nume} {p.prenume}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* MAȘINI */}
          {allCars.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="small-label flex items-center gap-2">
                  <CarIcon size={14}/> Autoturisme atribuite
                  {form.car_ids.length > 0 && <span className="text-[10px] font-bold text-[var(--primary)]">({form.car_ids.length})</span>}
                </Label>
              </div>
              {suggestions.cars.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5 p-2 bg-amber-50/60 border border-amber-200 rounded-md" data-testid="car-suggestions">
                  {suggestions.cars.slice(0, 6).map((s) => {
                    const sel = form.car_ids.includes(s.car_id);
                    return (
                      <button
                        key={s.car_id}
                        type="button"
                        onClick={() => toggleCar(s.car_id)}
                        className={`text-[10px] px-2 py-1 rounded-full border transition-all ${sel ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white text-zinc-700 border-amber-300 hover:bg-amber-100"}`}
                        data-testid={`car-suggest-${s.car_id}`}
                      >
                        {s.name} <span className="opacity-60">×{s.count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto border border-[var(--border)] rounded-md p-2" data-testid="car-list">
                {allCars.filter((c) => c.status !== "defect" && c.status !== "sold").map((c) => {
                  const sel = form.car_ids.includes(c.id);
                  const suggested = suggestedCarIds.includes(c.id);
                  const plate = c.numar_inmatriculare || (c.intern_number ? `#${c.intern_number}` : "");
                  const mm = `${c.marca || ""} ${c.model || ""}`.trim();
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCar(c.id)}
                      data-testid={`schedule-car-${c.id}`}
                      title={`${plate}${plate && mm ? " · " : ""}${mm}`}
                      className={`text-left text-xs px-2 py-1 rounded transition-colors flex items-start gap-1.5 ${sel ? "bg-[var(--primary)] text-white" : suggested ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-zinc-50"}`}
                    >
                      {suggested && <Sparkles size={10} className={`mt-0.5 shrink-0 ${sel ? "text-white" : "text-amber-500"}`}/>}
                      <span className="min-w-0 flex-1">
                        <span className="block font-mono text-[10px] font-bold leading-tight truncate">{plate || "—"}</span>
                        {mm && <span className={`block text-[10px] leading-tight truncate ${sel ? "opacity-90" : "text-zinc-500"}`}>{mm}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="small-label">👥 Necesar minim persoane</Label>
              <Input type="number" min={0} value={form.min_people} onChange={(e) => setForm({ ...form, min_people: e.target.value })} className="mt-1" placeholder="ex: 3" data-testid="schedule-min-people"/>
            </div>
          </div>

          <div><Label className="small-label">Notă</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2" /></div>
          {tab === "project" && (
            <ResourcePreview start={form.start_date} end={form.end_date} projectId={form.project_id} projects={projects} dailyNeeds={dailyNeeds} month={month} editingEntryId={entry?.id}/>
          )}
        </div>
        <DialogFooter>
          <div className="flex items-center gap-2 w-full">
            {mode === "edit" && <Button variant="destructive" onClick={remove}><Trash2 size={14} className="mr-1"/>Șterge</Button>}
            <div className="flex-1" />
            <Button variant="outline" onClick={onClose}>Anulează</Button>
            <Button onClick={submit} className="bg-[var(--primary)]" data-testid="submit-schedule">{mode === "edit" ? "Salvează" : "Adaugă"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
